import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { OrderRoomOccupant } from '../../types/orderRoomOccupant';
import { APIHOST } from '../../common';
import {useApp} from "../../components/AppContext";
import { set } from 'date-fns';

interface EditOrderRoomOccupantDialogProps {
  open: boolean;
  onClose: () => void;
  occupant?: OrderRoomOccupant | null;
  orderRoomId: string;
  maxOccupants?: number;
  currentOccupantCount?: number;
  onSave: () => void;
}

// API helper function to get authenticated headers
const getAuthHeaders = () => {
  const authToken = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(authToken && { Authorization: `Bearer ${authToken}` })
  };
};

const EditOrderRoomOccupantDialog = ({
  open,
  onClose,
  occupant,
  orderRoomId,
  maxOccupants = 4,
  currentOccupantCount = 0,
  onSave
}: EditOrderRoomOccupantDialogProps) => {
  const [saving, setSaving] = useState(false);
  const [occupantForm, setOccupantForm] = useState({
    firstName: '',
    lastName: ''
  });
  const { setSnackbarInput } = useApp();
  // Reset form when dialog opens/closes or occupant changes
  useEffect(() => {
    if (open) {
      setOccupantForm({
        firstName: occupant?.firstName || '',
        lastName: occupant?.lastName || ''
      });
    } else {
      setOccupantForm({ firstName: '', lastName: '' });
    }
  }, [open, occupant]);

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  // API functions for occupant management
  const createOccupantInAPI = async (occupantData: Omit<OrderRoomOccupant, 'id'>, roomId: string) => {
    try {
      const response = await fetch(`${APIHOST}/api/order-room-occupants`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          data: {
            ...occupantData,
            order_room: roomId
          }
        })
      });
      
      const res = await response.json();
      if (!response.ok) {
        setSnackbarInput({ message: res.error?.message || 'Failed to add occupant', severity: 'error' });
        return res;
      }
      
      setSnackbarInput({ message: 'Occupant added successfully', severity: 'success' });
      return res;
    } catch (error) {
     
      setSnackbarInput({ message: 'Failed to add occupant', severity: 'error' });

      throw error;
    }
  };

  const updateOccupantInAPI = async (documentId: string, occupantData: Partial<OrderRoomOccupant>) => {
    try {
      const response = await fetch(`${APIHOST}/api/order-room-occupants/${documentId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          data: occupantData
        })
      });
      
      if (!response.ok) throw new Error('Failed to update occupant');
      setSnackbarInput({ message: 'Occupant updated successfully', severity: 'success' });
      return await response.json();
    } catch (error) {
      setSnackbarInput({ message: 'Failed to update occupant', severity: 'error' });
      throw error;
    }
  };

  const handleSave = async () => {
    if (!occupantForm.firstName.trim() || !occupantForm.lastName.trim()) {
      alert('Please fill in both first and last name.');
      return;
    }

    // Check max occupants constraint for new occupants
    if (!occupant && currentOccupantCount >= maxOccupants) {
      alert(`Cannot add more occupants. Maximum allowed for this room type is ${maxOccupants}.`);
      return;
    }

    setSaving(true);
    try {
      if (occupant) {
        // Edit existing occupant
        if (occupant.documentId) {
          await updateOccupantInAPI(occupant.documentId, {
            firstName: occupantForm.firstName,
            lastName: occupantForm.lastName
          });
        }
      } else {
        // Add new occupant
        await createOccupantInAPI({
          firstName: occupantForm.firstName,
          lastName: occupantForm.lastName
        }, orderRoomId);
      }

      onSave(); // Notify parent to refresh data
      handleClose();
    } catch (error) {
      console.error('Error saving occupant:', error);
      alert('An error occurred while saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {occupant ? 'Edit Occupant' : 'Add Occupant'}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          disabled={saving}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="First Name"
            value={occupantForm.firstName}
            onChange={(e) => setOccupantForm(prev => ({ ...prev, firstName: e.target.value }))}
            fullWidth
            required
            disabled={saving}
          />
          <TextField
            label="Last Name"
            value={occupantForm.lastName}
            onChange={(e) => setOccupantForm(prev => ({ ...prev, lastName: e.target.value }))}
            fullWidth
            required
            disabled={saving}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving}
        >
          {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {occupant ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrderRoomOccupantDialog;